import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Palette, ExternalLink } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

const CANVA_TEMPLATES: Record<string, string> = {
  instagram_post:   'https://www.canva.com/design/create?category=instagram_post',
  instagram_story:  'https://www.canva.com/design/create?category=instagram_story',
  twitter_post:     'https://www.canva.com/design/create?category=twitter_post',
  linkedin_post:    'https://www.canva.com/design/create?category=linkedin_post',
  facebook_post:    'https://www.canva.com/design/create?category=facebook_post',
  media_kit:        'https://www.canva.com/design/create?category=press_kit',
  presentation:     'https://www.canva.com/design/create?category=presentation',
  flyer:            'https://www.canva.com/design/create?category=flyer',
}

const DESIGN_TYPES = [
  { value: 'instagram_post',  label: 'Instagram Post (1080×1080)' },
  { value: 'instagram_story', label: 'Instagram Story (1080×1920)' },
  { value: 'twitter_post',    label: 'Twitter / X Post' },
  { value: 'linkedin_post',   label: 'LinkedIn Post' },
  { value: 'facebook_post',   label: 'Facebook Post' },
  { value: 'media_kit',       label: 'Media Kit / Press Kit' },
  { value: 'presentation',    label: 'Presentation' },
  { value: 'flyer',           label: 'Flyer / Poster' },
]

interface CanvaDesignButtonProps {
  defaultType?: string
  label?: string
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'default' | 'sm' | 'lg'
}

export function CanvaDesignButton({
  defaultType = 'instagram_post',
  label = 'Design in Canva',
  variant = 'outline',
  size = 'sm',
}: CanvaDesignButtonProps) {
  const [open, setOpen] = useState(false)
  const [designType, setDesignType] = useState(defaultType)
  const [title, setTitle] = useState('')
  const { toast } = useToast()

  const handleOpen = () => {
    const url = CANVA_TEMPLATES[designType] ?? CANVA_TEMPLATES.instagram_post
    const finalUrl = title.trim()
      ? `${url}&title=${encodeURIComponent(title.trim())}`
      : url
    window.open(finalUrl, '_blank', 'noopener,noreferrer')
    setOpen(false)
    toast({
      title: 'Opening Canva',
      description: 'Design your graphic and download it to upload here.',
    })
  }

  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOpen(true)}>
        <Palette className="w-3.5 h-3.5 mr-1.5" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Design in Canva</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label>Design type</Label>
              <Select value={designType} onValueChange={setDesignType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DESIGN_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Design title (optional)</Label>
              <Input
                placeholder="e.g. Launch campaign graphic"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
            </div>
            <p className="text-justify text-xs text-muted-foreground">
              Canva will open in a new tab. Once finished, download your design and upload it here as media.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleOpen}>
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Open Canva
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
